import sharedStyles from "../../styles.module.scss"
import styles from "./styles.module.scss"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import { useEditor, EditorContent, Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Underline from "@tiptap/extension-underline"
import classNames from "classnames"
import { useRef, useEffect, useMemo } from "react"
import ChevronDownFill from "~icons/eva/chevron-down-fill"
import { Popover } from "react-tiny-popover"
import BulletListIcon from "../../../../../../assets/icons/BulletListIcon"
import OrderedListIcon from "../../../../../../assets/icons/OrderedListIcon"
import LinkIcon from "../../../../../../assets/icons/LinkIcon"
import { useMutation } from "@tanstack/react-query"
import { upsertAgentProfile } from "../../../../../../../services/firebase-functions/agentProfile"
import { useStores } from "../../../../../../../models/helpers/useStores"
import { AccountModalFooter } from "../../components/Footer"

const extensions = [
  StarterKit,
  Underline,
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: "https",
    protocols: ["http", "https"],
    isAllowedUri: (url, ctx) => {
      try {
        const parsedUrl = url.includes(":")
          ? new URL(url)
          : new URL(`${ctx.defaultProtocol}://${url}`)

        if (!ctx.defaultValidate(parsedUrl.href)) {
          return false
        }

        const disallowedProtocols = ["ftp", "file", "mailto"]
        const protocol = parsedUrl.protocol.replace(":", "")

        if (disallowedProtocols.includes(protocol)) {
          return false
        }

        const allowedProtocols = ctx.protocols.map((p) => (typeof p === "string" ? p : p.scheme))

        if (!allowedProtocols.includes(protocol)) {
          return false
        }

        return true
      } catch {
        return false
      }
    },
  }),
]

class TextSelectorState {
  isOpen: boolean

  constructor() {
    this.isOpen = false

    makeAutoObservable(this)
  }

  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen
  }
}

const TextSelector = observer(({ editor }: { editor: Editor }) => {
  const store = useMemo(() => new TextSelectorState(), [])

  const getActiveText = () => {
    if (editor.isActive("heading", { level: 1 })) {
      return "Heading 1"
    }
    if (editor.isActive("heading", { level: 2 })) {
      return "Heading 2"
    }
    return "Text"
  }

  const activeText = getActiveText()

  return (
    <Popover
      isOpen={store.isOpen}
      positions={["bottom"]}
      align="start"
      padding={10}
      onClickOutside={() => store.setIsOpen(false)}
      containerStyle={{
        zIndex: "1000",
        marginLeft: "-5px",
      }}
      content={
        <div className={styles.bubbleMenuTextPopover}>
          <button
            className={styles.bubbleMenuTextPopoverItem}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 1 }).run()
              store.setIsOpen(false)
            }}
          >
            <div className={styles.bubbleMenuTextPopoverItemIcon}>H1</div>
            <p>Heading 1</p>
          </button>
          <button
            className={styles.bubbleMenuTextPopoverItem}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 2 }).run()
              store.setIsOpen(false)
            }}
          >
            <div className={styles.bubbleMenuTextPopoverItemIcon}>H2</div>
            <p>Heading 2</p>
          </button>
          <button
            className={styles.bubbleMenuTextPopoverItem}
            onClick={() => {
              editor.chain().focus().setParagraph().run()
              store.setIsOpen(false)
            }}
          >
            <div className={styles.bubbleMenuTextPopoverItemIcon}>T</div>
            <p>Text</p>
          </button>
        </div>
      }
    >
      <button
        className={styles.bubbleMenuTextSelect}
        onClick={() => store.setIsOpen(!store.isOpen)}
      >
        <p className={styles.bubbleMenuTextSelectText}>{activeText}</p>
        <ChevronDownFill className={styles.bubbleMenuTextSelectIcon} />
      </button>
    </Popover>
  )
})

class LinkButtonState {
  isOpen: boolean
  link: string

  constructor() {
    this.isOpen = false
    this.link = ""

    makeAutoObservable(this)
  }

  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen
  }

  setLink(link: string) {
    this.link = link
  }
}

const LinkButton = observer(({ editor }: { editor: Editor }) => {
  const store = useMemo(() => new LinkButtonState(), [])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (store.isOpen && inputRef.current) {
      setTimeout(() => {
        const previousUrl = editor.getAttributes("link").href
        store.setLink(previousUrl)
        inputRef.current?.focus()
      }, 50)
    }

    if (!store.isOpen) {
      if (store.link) {
        editor.chain().focus().extendMarkRange("link").setLink({ href: store.link }).run()
      } else {
        editor.chain().focus().extendMarkRange("link").unsetLink().run()
      }
    }
  }, [store.isOpen])

  return (
    <Popover
      isOpen={store.isOpen}
      positions={["top"]}
      align="center"
      padding={10}
      onClickOutside={() => store.setIsOpen(false)}
      containerStyle={{
        zIndex: "1000",
      }}
      content={
        <div className={styles.linkInputContainer}>
          <input
            ref={inputRef}
            type="text"
            value={store.link}
            onChange={(e) => store.setLink(e.target.value)}
            className={styles.linkTextInput}
          />
          <button
            className={styles.linkSaveButton}
            onClick={() => {
              // Apply the link and close the popover
              if (editor) {
                editor.chain().focus().extendMarkRange("link").setLink({ href: store.link }).run()
              }
              store.setIsOpen(false)
            }}
          >
            Save
          </button>
        </div>
      }
    >
      <button
        onClick={() => {
          store.setIsOpen(!store.isOpen)
        }}
        className={classNames(styles.bubbleMenuButton, {
          [styles.bubbleMenuButtonActive]: editor.isActive("link"),
        })}
      >
        <LinkIcon />
      </button>
    </Popover>
  )
})

class TermsAndConditionsTabStore {
  editorHeight: number

  constructor() {
    this.editorHeight = 0

    makeAutoObservable(this)
  }

  setEditorHeight(editorHeight: number) {
    this.editorHeight = editorHeight
  }
}

export const TermsAndConditionsTab = observer(
  ({
    closeModal,
    setIsModalWarningVisible,
    setIsContentChanged,
  }: {
    closeModal: () => void
    setIsModalWarningVisible: (value: boolean) => void
    setIsContentChanged: (value: boolean) => void
  }) => {
    const rootStore = useStores()
    const { loggedInUser, agentProfile } = rootStore
    const store = useMemo(() => new TermsAndConditionsTabStore(), [])
    const textAreaContainerRef = useRef(null)

    const headingHeight = 30
    useEffect(() => {
      if (textAreaContainerRef.current) {
        const containerHeight = textAreaContainerRef.current.clientHeight
        store.setEditorHeight(containerHeight - 10 - headingHeight)
      }
    }, [])

    const editor = useEditor({
      extensions,
      content: agentProfile?.customTerms || "",
      editorProps: {
        attributes: {
          class: styles.tiptapTextArea,
          style: `height: ${store.editorHeight}px; max-height: ${store.editorHeight}px`,
        },
      },
    })

    const customTerms = editor?.getHTML()
    const isEmpty = customTerms === "<p></p>"
    const isDirty = agentProfile?.customTerms !== customTerms && !isEmpty

    useEffect(() => {
      setIsContentChanged(isDirty)
    }, [isDirty])

    const handleCloseModal = () => {
      if (isDirty) {
        setIsModalWarningVisible(true)
        return
      }

      closeModal()
    }

    const { mutate: updateCustomTerms, isPending } = useMutation({
      mutationFn: async () => {
        const updatedAgentProfile = await upsertAgentProfile({
          agentProfile: {
            id: agentProfile.id,
            userId: loggedInUser.id,
            customTerms,
          },
        })

        return updatedAgentProfile
      },
      onSuccess: (updatedAgentProfile) => {
        if (updatedAgentProfile) {
          rootStore.setProp("agentProfile", updatedAgentProfile)
        }
      },
    })

    return (
      <>
        <div className={sharedStyles.accountModalBody}>
          <div className={classNames(sharedStyles.agencyInfoContainer, styles.fullHeight)}>
            <div className={sharedStyles.sectionInfo}>
              <p className={sharedStyles.sectionTitle}>Terms & Conditions</p>
              <p className={sharedStyles.sectionDescription}>
                Customize your general agreement requirements.
              </p>
            </div>
            <div className={classNames(sharedStyles.agencyInfoForm, styles.fullHeight)}>
              <div
                className={classNames(styles.textAreaContainer, styles.fullHeight)}
                ref={textAreaContainerRef}
              >
                {editor && (
                  <div className={styles.bubbleMenu}>
                    <TextSelector editor={editor} />
                    <div className={styles.bubbleMenuSeparator} />
                    <button
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={classNames(styles.bubbleMenuButton, styles.bubbleMenuButtonBold, {
                        [styles.bubbleMenuButtonActive]: editor.isActive("bold"),
                      })}
                    >
                      B
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={classNames(styles.bubbleMenuButton, {
                        [styles.bubbleMenuButtonActive]: editor.isActive("italic"),
                      })}
                    >
                      /
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      className={classNames(
                        styles.bubbleMenuButton,
                        styles.bubbleMenuButtonUnderline,
                        {
                          [styles.bubbleMenuButtonActive]: editor.isActive("underline"),
                        },
                      )}
                    >
                      U
                    </button>
                    <div className={styles.bubbleMenuSeparator} />
                    <button
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={classNames(styles.bubbleMenuButton, {
                        [styles.bubbleMenuButtonActive]: editor.isActive("bulletList"),
                      })}
                    >
                      <BulletListIcon />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      className={classNames(styles.bubbleMenuButton, {
                        [styles.bubbleMenuButtonActive]: editor.isActive("orderedList"),
                      })}
                    >
                      <OrderedListIcon />
                    </button>
                    <LinkButton editor={editor} />
                  </div>
                )}
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>
        </div>
        <AccountModalFooter
          isPending={isPending}
          isSaveDisabled={!isDirty}
          saveButtonLabel={agentProfile?.customTerms ? "Save changes" : "Add terms"}
          onCancel={() => handleCloseModal()}
          onSave={() => updateCustomTerms()}
        />
      </>
    )
  },
)
